import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import isEmpty from 'is-empty';

import {Dispatch, Store} from 'spectra-logic-ui';
import {fetchResource} from 'spectra-logic-ui/actions';
import {Card, SlidePanel, Table} from 'spectra-logic-ui/components';
import AccountIcon from 'spectra-logic-ui/icons/AccountCircle';

import {Account, LifecycleV2} from '@/types';
import ButtonToolbar from '@/components/button_toolbar';
import CardHeader from '@/components/card_header';
import DeleteDialog from '@/components/delete_dialog';
import PropertiesDetails from '@/accounts/details/properties';
import MetricsDetails from '@/accounts/details/metrics';
import CreateAccountDialog from '@/accounts/form/add';
import EditAccountDialog from '@/accounts/form/edit';
import {System} from '@/system/types';

type Props = {
  accounts?: Account[];
  error?: boolean;
  fetching?: boolean;
  fetchAccounts?: () => Promise<any>;
  lifecycles?: LifecycleV2[];
  fetchLifecycles?: () => Promise<any>;
  system?: System;
  fetchSystem?: () => Promise<any>;
}

const descriptionStyles = {
  maxWidth: 240,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const Accounts = (props: Props) => {
  const {accounts = [], error = false, fetching = false, fetchAccounts, lifecycles = [], fetchLifecycles,
    system={} as System, fetchSystem} = props;
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [isOpenDetails, setOpenDetails] = useState(false);
  const closeDetails = () => setOpenDetails(false);

  useEffect(() => {
    if (fetchAccounts !== undefined) {
      fetchAccounts();
    }
    if (fetchLifecycles !== undefined) {
      fetchLifecycles();
    }
    if (fetchSystem !== undefined) {
      fetchSystem();
    }
  }, []);

  useEffect(() => {
    if (selectedAccountId && !accounts.find((account) => account.id === selectedAccountId)) {
      setSelectedAccountId('');
    }
  }, [accounts]);

  const onDelete = () => {
    setSelectedAccountId('');
  };

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId) || {} as Account;

  return (
    <Card>
      <CardHeader icon={AccountIcon}>
        IAM Accounts
      </CardHeader>
      <Card.Body sx={{position: 'relative'}}>
        <ButtonToolbar sx={{position: 'sticky', top: 80}}>
          <ButtonToolbar.CreateDialogButton
            title='Add'
            dialog={(props: any) => <CreateAccountDialog lifecycles={lifecycles} {...props} />}
          />
          <ButtonToolbar.EditDialogButton
            disabled={isEmpty(selectedAccount)} onClick={closeDetails}
            dialog={(props: any) => (
              <EditAccountDialog account={selectedAccount} lifecycles={lifecycles} {...props} />
            )}
          />
          <ButtonToolbar.DeleteDialogButton
            disabled={isEmpty(selectedAccount)} onSuccess={onDelete} onClick={closeDetails}
            dialog={(props: any) => (
              <DeleteDialog
                id={selectedAccountId} name={selectedAccountId}
                title='Delete IAM Account' resource='accounts' {...props}
              />
            )}
          />
        </ButtonToolbar>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Cell>ID</Table.Cell>
              <Table.Cell>Description</Table.Cell>
              <Table.Cell>Type</Table.Cell>
              <Table.Cell>Canonical ID</Table.Cell>
              <Table.Cell>Connection Limit</Table.Cell>
              <Table.Cell>Status</Table.Cell>
              <Table.Cell></Table.Cell>
            </Table.Row>
          </Table.Header>
          <Table.Body isLoading={fetching} hasError={error}>
            {accounts.map((account) => (
              <Table.Row
                key={account.id} selected={account.id === selectedAccountId}
                onClick={() => setSelectedAccountId(account.id)}
              >
                <Table.Cell>{account.id}</Table.Cell>
                <Table.Cell sx={descriptionStyles}>{account.description}</Table.Cell>
                <Table.Cell>{account.local ? 'Local' : 'AWS'}</Table.Cell>
                <Table.Cell>{account.canonicalId}</Table.Cell>
                <Table.Cell>{account.connectionLimit !== undefined ? account.connectionLimit + '%' : ''}</Table.Cell>
                <Table.Cell>{account.inactive ? 'Inactive' : 'Active'}</Table.Cell>
                <Table.CellDetailsButton onClick={() => setOpenDetails(true)} />
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        <SlidePanel
          title={selectedAccount.description || selectedAccount.id} options={['Properties', 'Usage']}
          open={isOpenDetails} onClose={closeDetails}
        >
          <PropertiesDetails account={selectedAccount} lifecycles={lifecycles} system={system} />
          <MetricsDetails id={selectedAccount.id}/>
        </SlidePanel>
      </Card.Body>
    </Card>
  );
};

const mapStateToProps = (state: Store) => {
  const accountsResource = state.resources.accounts || {};
  const lifecyclesResource = state.resources.lifecycles || {};
  const systemResource = state.resources.system || {};
  return {
    accounts: accountsResource.data,
    accountsError: accountsResource.error,
    accountsFetching: accountsResource.fetching,
    lifecycles: lifecyclesResource.data,
    system: systemResource.data,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchAccounts: () => dispatch(fetchResource('accounts')),
  fetchLifecycles: () => dispatch(fetchResource('lifecycles')),
  fetchSystem: () => dispatch(fetchResource('system')),
});

export default connect(mapStateToProps, mapDispatchToProps)(Accounts);
